// https://developers.google.com/youtube/iframe_api_reference
// https://github.com/vimeo/player.js

if (!customElements.get("video-hero")) {
  customElements.define(
    "video-hero",
    class VideoHero extends HTMLElement {
      constructor() {
        super();
        this.player = null;
        this.raf = null;
      }

      connectedCallback() {
        // Defer video loading until element is visible (lazy load)
        this.setupLazyLoad();
      }

      setupLazyLoad() {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                observer.disconnect();
                this.init();
              }
            });
          },
          { rootMargin: "200px 0px" } // Load 200px before visible
        );
        observer.observe(this);
      }

      init() {
        switch (this.dataset.type) {
          case "youtube":
            this.initYoutubeVideo();
            break;
          case "vimeo":
            this.initVimeoVideo();
            break;
          case "mp4":
            this.initMp4Video();
            break;
        }
      }

      /* ---------------- MP4 VIDEO ---------------- */
      initMp4Video() {
        const player = this.querySelector("video");
        const controlsWrapper = this.querySelector(".video-controls-button");
        if (!player) return;
        this.player = player;

        let raf;
        const animateProgress = () => {
          if (!player.duration) return;
          const percent = Math.min(
            99.9,
            (player.currentTime / player.duration) * 100
          );
          const circle = controlsWrapper?.querySelector(".circle");
          if (circle) circle.setAttribute("stroke-dasharray", `${percent}, 100`);
          if (!player.paused && !player.ended) {
            raf = requestAnimationFrame(animateProgress);
          }
        };

        if (controlsWrapper) {
          const toggleBtn = controlsWrapper.querySelector(".video-toggle");
          const iconPlay = controlsWrapper.querySelector(".icon-play");
          const iconPause = controlsWrapper.querySelector(".icon-pause");

          const updateIcons = () => {
            const isPlaying = !player.paused && !player.ended;
            iconPlay.style.display = isPlaying ? "none" : "inline";
            iconPause.style.display = isPlaying ? "inline" : "none";
          };

          toggleBtn.addEventListener("click", () => {
            if (player.paused) player.play();
            else player.pause();
            updateIcons();
          });

          player.addEventListener("play", () => {
            cancelAnimationFrame(raf);
            animateProgress();
            updateIcons();
          });

          player.addEventListener("pause", () => {
            cancelAnimationFrame(raf);
            updateIcons();
          });

          player.addEventListener("ended", () => {
            cancelAnimationFrame(raf);
            player.currentTime = 0;
            player.play();
          });

          player.addEventListener("loadedmetadata", () => {
            updateIcons();
            requestAnimationFrame(animateProgress);
          });

          // Autoplay attempt
          player
            .play()
            .then(() => {
              updateIcons();
              requestAnimationFrame(animateProgress);
            })
            .catch(() => {
              updateIcons();
              player.setAttribute("controls", "");
            });

          // Ensure icons visible immediately
          updateIcons();
        }

        // Intersection Observer
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) player.play().catch(() => {});
              else player.pause();
            });
          },
          { rootMargin: "0px 0px 50px 0px" }
        );
        observer.observe(player);
      }

      /* ---------------- SCRIPT LOADER ---------------- */
      loadScript(videoType) {
        return new Promise((resolve, reject) => {
          if (videoType === "youtube" && window.YT && window.YT.Player) return resolve();
          if (videoType === "vimeo" && window.Vimeo) return resolve();

          const script = document.createElement("script");
          script.onload = resolve;
          script.onerror = reject;
          script.async = true;
          script.src =
            videoType === "youtube"
              ? "https://www.youtube.com/iframe_api"
              : "https://player.vimeo.com/api/player.js";
          document.body.appendChild(script);
        });
      }

      /* ---------------- YOUTUBE VIDEO ---------------- */
      initYoutubeVideo() {
        this.loadScript("youtube").then(() => {
          if (window.YT && window.YT.Player) {
            this.createYoutubePlayer();
          } else {
            if (!window._youtubePlayers) window._youtubePlayers = [];
            window._youtubePlayers.push(this);

            if (!window.onYouTubeIframeAPIReady) {
              window.onYouTubeIframeAPIReady = () => {
                window._youtubePlayers.forEach((instance) =>
                  instance.createYoutubePlayer()
                );
                window._youtubePlayers = [];
              };
            }
          }
        });
      }

      createYoutubePlayer() {
        const videoId = this.dataset.videoId;
        const element = document.createElement("div");
        this.appendChild(element);

        this.player = new YT.Player(element, {
          videoId: videoId,
          playerVars: {
            showinfo: 0,
            controls: 0,
            fs: 0,
            rel: 0,
            height: "100%",
            width: "100%",
            iv_load_policy: 3,
            html5: 1,
            loop: 1,
            playsinline: 1,
            modestbranding: 1,
            disablekb: 1,
          },
          events: {
            onReady: () => this.onYoutubeReady(),
            onStateChange: (event) => this.onYoutubeStateChange(event),
          },
        });
      }

      onYoutubeReady() {
        this.iframe = this.querySelector("iframe");
        if (this.iframe) this.iframe.setAttribute("tabindex", "-1");
        this.player.mute();
        this.player.playVideo();

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              entry.isIntersecting ? this.youtubePlay() : this.youtubePause();
            });
          },
          { rootMargin: "0px 0px 50px 0px" }
        );

        if (this.iframe) observer.observe(this.iframe);
      }

      onYoutubeStateChange(event) {
        if (event.data === YT.PlayerState.ENDED) this.youtubePlay();
      }

      youtubePlay() {
        this.player?.playVideo();
      }

      youtubePause() {
        this.player?.pauseVideo();
      }

      /* ---------------- VIMEO VIDEO ---------------- */
      initVimeoVideo() {
        this.loadScript("vimeo").then(() => this.onVimeoIframeAPIReady());
      }

      onVimeoIframeAPIReady() {
        const videoId = this.dataset.videoId;
        this.player = new Vimeo.Player(this, {
          id: videoId,
          autoplay: true,
          autopause: false,
          background: false,
          controls: false,
          loop: true,
          height: "100%",
          width: "100%",
        });
        this.player.ready().then(() => this.onVimeoReady());
      }

      onVimeoReady() {
        this.iframe = this.querySelector("iframe");
        if (this.iframe) this.iframe.setAttribute("tabindex", "-1");
        this.player.setMuted(true);

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              entry.isIntersecting ? this.vimeoPlay() : this.vimeoPause();
            });
          },
          { rootMargin: "0px 0px 50px 0px" }
        );
        if (this.iframe) observer.observe(this.iframe);
      }

      vimeoPlay() {
        this.player?.play();
      }

      vimeoPause() {
        this.player?.pause();
      }
    }
  );
}
